import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getStats, getCases, getScheduler, runJob } from '../lib/api';

const s = {
  page: { padding: 24, overflowY: 'auto', flex: 1 },
  title: { fontSize: 18, fontWeight: 500, marginBottom: 20 },
  metrics: { display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0,1fr))', gap: 12, marginBottom: 24 },
  metric: { background: 'var(--surface)', border: '0.5px solid var(--border)', borderRadius: 10, padding: '16px' },
  mLabel: { fontSize: 11, color: 'var(--text3)', letterSpacing: '0.06em', marginBottom: 8 },
  mValue: { fontSize: 26, fontWeight: 500, color: 'var(--gold)' },
  mSub: { fontSize: 11, color: 'var(--text3)', marginTop: 3 },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { fontSize: 10, color: 'var(--text3)', letterSpacing: '0.08em', textAlign: 'left', padding: '8px 12px', borderBottom: '0.5px solid var(--border)' },
  td: { fontSize: 13, padding: '12px', borderBottom: '0.5px solid var(--border)', verticalAlign: 'middle' },
  sev: { width: 6, height: 28, borderRadius: 3 },
  badge: { fontSize: 11, padding: '3px 8px', borderRadius: 7 },
};

const sevColor = { CRITICAL: 'var(--red)', HIGH: 'var(--amber)', MEDIUM: 'var(--blue)', LOW: 'var(--green)' };
const statusStyle = {
  OPEN: { background: 'var(--amber-bg)', color: 'var(--amber)' },
  CLOSED: { background: 'var(--green-bg)', color: 'var(--green)' },
  IN_PROGRESS: { background: 'var(--blue-bg)', color: 'var(--blue)' },
};


function SchedulerPanel() {
  const [status, setStatus] = React.useState(null);
  const [running, setRunning] = React.useState({});

  React.useEffect(() => {
    getScheduler().then(setStatus).catch(() => {});
    const id = setInterval(() => getScheduler().then(setStatus).catch(() => {}), 30000);
    return () => clearInterval(id);
  }, []);

  const trigger = async (job) => {
    setRunning(r => ({ ...r, [job]: true }));
    try { await runJob(job); } catch {}
    setTimeout(() => { setRunning(r => ({ ...r, [job]: false })); getScheduler().then(setStatus).catch(() => {}); }, 2000);
  };

  if (!status) return null;
  const jobs = status.jobs || {};

  const fmtRun = (ts) => {
    if (!ts || ts === 'Never') return 'Never';
    try {
      const d = new Date(ts.endsWith('Z') ? ts : ts + 'Z');
      return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) + ' ' +
             d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    } catch { return ts.slice(0, 16).replace('T', ' '); }
  };

  return (
    <div style={{ background: 'var(--surface)', border: '0.5px solid var(--border)', borderRadius: 10, padding: '16px 20px' }}>
      <div style={{ fontWeight: 500, color: 'var(--text)', marginBottom: 14, fontSize: 13 }}>Scheduled monitoring</div>
      {Object.entries(jobs).map(([key, job]) => (
        <div key={key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 0', borderBottom: '0.5px solid var(--border)' }}>
          <div>
            <div style={{ fontSize: 12, color: 'var(--text2)' }}>{job.description}</div>
            <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>
              Last: {fmtRun(job.last_run)} · Next: {job.next_run}
              {job.active === false && ' · Inactive (API key needed)'}
            </div>
          </div>
          <button
            onClick={() => trigger(key)}
            disabled={running[key]}
            style={{ fontSize: 11, background: 'var(--raised)', border: '0.5px solid var(--border)', color: running[key] ? 'var(--text3)' : 'var(--text2)', borderRadius: 5, padding: '4px 10px', cursor: 'pointer' }}>
            {running[key] ? 'Running...' : 'Run now'}
          </button>
        </div>
      ))}
    </div>
  );
}

export function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const navigate = useNavigate();

  useEffect(() => { getStats().then(setStats).catch(() => {}); }, []);

  return (
    <div style={s.page}>
      <div style={s.title}>Overview</div>
      <div style={s.metrics}>
        <div style={s.metric}><div style={s.mLabel}>TOTAL CLIENTS</div><div style={s.mValue}>{stats?.clients ?? '—'}</div><div style={s.mSub}>Registered</div></div>
        <div style={s.metric}><div style={s.mLabel}>OPEN CASES</div><div style={{ ...s.mValue, color: stats?.open_cases > 0 ? 'var(--red)' : 'var(--green)' }}>{stats?.open_cases ?? '—'}</div><div style={s.mSub}>Active incidents</div></div>
        <div style={s.metric}><div style={s.mLabel}>TOTAL CASES</div><div style={s.mValue}>{stats?.total_cases ?? '—'}</div><div style={s.mSub}>All time</div></div>
        <div style={s.metric}><div style={s.mLabel}>SERVER STATUS</div><div style={{ ...s.mValue, fontSize: 16, paddingTop: 4, color: 'var(--green)' }}>{stats ? 'Online' : '—'}</div><div style={s.mSub}>{stats?.timestamp?.slice(0, 10) || ''}</div></div>
      </div>
      <div style={{ fontSize: 13, color: 'var(--text2)', background: 'var(--surface)', border: '0.5px solid var(--border)', borderRadius: 10, padding: '16px 20px', marginBottom: 16 }}>
        <div style={{ fontWeight: 500, color: 'var(--text)', marginBottom: 10 }}>Quick actions</div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          {[
            { label: 'Add a new client', onClick: () => navigate('/clients') },
            { label: 'View open incidents', onClick: () => navigate('/incidents') },
            { label: 'Review signups', onClick: () => navigate('/signups') },
            { label: 'Open inbox', onClick: () => navigate('/inbox') },
          ].map(a => (
            <button key={a.label} onClick={a.onClick} style={{ fontSize: 12, background: 'var(--raised)', border: '0.5px solid var(--border)', borderRadius: 6, padding: '6px 12px', color: 'var(--text2)', cursor: 'pointer' }}>{a.label}</button>
          ))}
        </div>
      </div>
      <SchedulerPanel />
    </div>
  );
}

export function AdminIncidents() {
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { getCases().then(d => setCases(Object.values(d.cases || d))).catch(() => setCases([])).finally(() => setLoading(false)); }, []);

  return (
    <div style={s.page}>
      <div style={s.title}>Incidents <span style={{ fontSize: 13, color: 'var(--text2)', fontWeight: 400 }}>({cases.length})</span></div>
      {loading ? <div style={{ color: 'var(--text2)', fontSize: 13 }}>Loading...</div> : cases.length === 0 ? (
        <div style={{ color: 'var(--text2)', fontSize: 13, textAlign: 'center', padding: 40 }}>No incidents on record</div>
      ) : (
        <table style={s.table}>
          <thead><tr>{['', 'Case ID', 'Client', 'Type', 'Severity', 'Status', 'Opened'].map(h => <th key={h} style={s.th}>{h.toUpperCase()}</th>)}</tr></thead>
          <tbody>
            {cases.map(c => (
              <tr key={c.case_id}>
                <td style={{ ...s.td, width: 10, paddingRight: 0 }}><div style={{ ...s.sev, background: sevColor[c.severity] || 'var(--text3)' }} /></td>
                <td style={{ ...s.td, fontFamily: 'monospace', fontSize: 11, color: 'var(--text2)' }}>{c.case_id}</td>
                <td style={{ ...s.td, fontWeight: 500 }}>{c.client_id}</td>
                <td style={{ ...s.td, color: 'var(--text2)' }}>{(c.incident_type || '').replace(/_/g, ' ')}</td>
                <td style={s.td}><span style={{ color: sevColor[c.severity] || 'var(--text2)', fontSize: 12 }}>{c.severity}</span></td>
                <td style={s.td}><span style={{ ...s.badge, ...statusStyle[c.status] || {} }}>{c.status}</span></td>
                <td style={{ ...s.td, color: 'var(--text2)', fontSize: 12 }}>{(c.created_at || '').slice(0, 10)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
